// Utility Provider Types - Managed list of power utility companies
// See implementation plan at ~/.claude/plans/tranquil-tinkering-blanket.md

/**
 * Country codes for utility provider locations
 */
export type CountryCode = 'US' | 'UK' | 'CA';

/**
 * Country display names
 */
export const COUNTRIES: Record<CountryCode, string> = {
  US: 'United States',
  UK: 'United Kingdom',
  CA: 'Canada',
};

/**
 * US State codes (target markets only)
 */
export const US_REGIONS = ['CA', 'NY', 'IL', 'GA', 'NM'] as const;
export type USRegion = typeof US_REGIONS[number];

export const US_REGION_NAMES: Record<USRegion, string> = {
  CA: 'California',
  NY: 'New York',
  IL: 'Illinois',
  GA: 'Georgia',
  NM: 'New Mexico',
};

/**
 * UK Regions (London focus)
 */
export const UK_REGIONS = ['London'] as const;
export type UKRegion = typeof UK_REGIONS[number];

/**
 * Canadian Provinces (target markets)
 */
export const CA_PROVINCES = ['BC', 'QC'] as const;
export type CAProvince = typeof CA_PROVINCES[number];

export const CA_PROVINCE_NAMES: Record<CAProvince, string> = {
  BC: 'British Columbia',
  QC: 'Quebec',
};

/**
 * All region codes union type
 */
export type RegionCode = USRegion | UKRegion | CAProvince;

/**
 * Get regions for a country code
 */
export function getRegionsForCountry(countryCode: CountryCode): readonly string[] {
  switch (countryCode) {
    case 'US':
      return US_REGIONS;
    case 'UK':
      return UK_REGIONS;
    case 'CA':
      return CA_PROVINCES;
    default:
      return [];
  }
}

/**
 * Get region display name
 */
export function getRegionName(countryCode: CountryCode, regionCode: string): string {
  if (countryCode === 'US') {
    return US_REGION_NAMES[regionCode as USRegion] || regionCode;
  }
  if (countryCode === 'CA') {
    return CA_PROVINCE_NAMES[regionCode as CAProvince] || regionCode;
  }
  return regionCode; // UK regions use their names directly
}

/**
 * Utility provider entity from database
 */
export interface UtilityProvider {
  id: string;
  name: string;
  countryCode: CountryCode;
  regionCode?: string;
  website?: string;
  description?: string;

  // Soft delete
  isDeleted: boolean;
  deletedAt?: string;
  deletedBy?: string;

  // Audit
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * Form data for creating/editing a utility provider
 */
export interface UtilityProviderFormData {
  name: string;
  countryCode: CountryCode;
  regionCode?: string;
  website?: string;
  description?: string;
}

/**
 * API response for utility provider list
 */
export interface UtilityProviderListResponse {
  providers: UtilityProvider[];
  lastUpdated: string;
  total: number;
}

/**
 * Bulk operation request for utility providers
 */
export interface BulkUtilityProviderRequest {
  action: 'softDelete' | 'restore' | 'hardDelete';
  providerIds: string[];
}

/**
 * Bulk operation response
 */
export interface BulkUtilityProviderResponse {
  success: number;
  failed: number;
  errors: Array<{
    providerId: string;
    error: string;
  }>;
}

// ============================================================================
// Transform Functions
// ============================================================================

/**
 * Transform database snake_case to camelCase
 */
export function transformProviderFromDb(
  dbProvider: Record<string, unknown>
): UtilityProvider {
  return {
    id: dbProvider.id as string,
    name: dbProvider.name as string,
    countryCode: dbProvider.country_code as CountryCode,
    regionCode: dbProvider.region_code as string | undefined,
    website: dbProvider.website as string | undefined,
    description: dbProvider.description as string | undefined,
    isDeleted: Boolean(dbProvider.is_deleted),
    deletedAt: dbProvider.deleted_at as string | undefined,
    deletedBy: dbProvider.deleted_by as string | undefined,
    createdAt: dbProvider.created_at as string,
    updatedAt: dbProvider.updated_at as string,
    createdBy: dbProvider.created_by as string | undefined,
    updatedBy: dbProvider.updated_by as string | undefined,
  };
}

/**
 * Transform camelCase to database snake_case
 */
export function transformProviderToDb(
  provider: UtilityProviderFormData
): Record<string, unknown> {
  return {
    name: provider.name,
    country_code: provider.countryCode,
    region_code: provider.regionCode || null,
    website: provider.website || null,
    description: provider.description || null,
  };
}

/**
 * Format provider for display (name + location)
 */
export function formatProviderDisplay(provider: UtilityProvider): string {
  const location = provider.regionCode
    ? `${getRegionName(provider.countryCode, provider.regionCode)}, ${COUNTRIES[provider.countryCode]}`
    : COUNTRIES[provider.countryCode];
  return `${provider.name} (${location})`;
}

/**
 * Format provider location for display
 */
export function formatProviderLocation(provider: UtilityProvider): string {
  if (!provider.regionCode) {
    return COUNTRIES[provider.countryCode];
  }
  return `${getRegionName(provider.countryCode, provider.regionCode)}, ${provider.countryCode}`;
}
